import React from 'react';
import {
  AbsoluteFill,
  Sequence,
  Easing,
  interpolate,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

const defaultScenes = [
  {
    type: 'intro',
    headline: 'Smooth Multi Screen',
    subline: 'Original Remotion intro',
    layout: 'split-panels',
  },
  {
    type: 'showcase',
    headline: 'Layered Motion',
    subline: 'Panels, typography, and soft transitions',
    layout: 'three-panels',
  },
  {
    type: 'cta',
    headline: 'Ready to Render',
    subline: 'Preview and edit in Remotion Studio',
    layout: 'center-cta',
  },
];

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

const fitText = (text, max = 76) => {
  const length = String(text || '').length;
  if (length > 80) return max * 0.46;
  if (length > 56) return max * 0.58;
  if (length > 36) return max * 0.74;
  return max;
};

const Grid = ({accent}) => {
  const frame = useCurrentFrame();
  const shift = frame * 0.55;

  return (
    <AbsoluteFill
      style={{
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.055) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.055) 1px, transparent 1px)
        `,
        backgroundSize: '72px 72px',
        backgroundPosition: `${-shift}px ${-shift}px`,
      }}
    >
      <div
        style={{
          position: 'absolute',
          right: 92,
          top: 86,
          width: 230,
          height: 230,
          borderRadius: 999,
          border: `3px solid ${accent}`,
          opacity: 0.24,
        }}
      />
    </AbsoluteFill>
  );
};

const MotionPanel = ({index, style}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const progress = interpolate(
    frame,
    [index * 0.16 * fps, (index * 0.16 + 0.85) * fps],
    [0, 1],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: Easing.bezier(0.16, 1, 0.3, 1),
    }
  );

  const x = interpolate(progress, [0, 1], [index % 2 === 0 ? -260 : 260, 0]);
  const opacity = interpolate(progress, [0, 1], [0, 1]);

  return (
    <div
      style={{
        position: 'absolute',
        transform: `translateX(${x}px)`,
        opacity,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.18)',
        boxShadow: '0 30px 90px rgba(0,0,0,0.32)',
        ...style,
      }}
    />
  );
};

const Scene = ({scene, index, accent, background}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const enter = interpolate(frame, [0, 0.75 * fps], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });

  const exit = interpolate(frame, [2.45 * fps, 3 * fps], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.bezier(0.7, 0, 0.84, 0),
  });

  const opacity = enter * exit;
  const y = interpolate(enter, [0, 1], [54, 0]);

  const headline = scene.headline || scene.title || 'Untitled Scene';
  const subline = scene.subline || scene.subtitle || '';
  const layout = scene.layout || 'split-panels';

  return (
    <AbsoluteFill
      style={{
        background,
        opacity,
      }}
    >
      <Grid accent={accent} />

      {layout === 'center-cta' ? (
        <>
          <MotionPanel
            index={0}
            style={{
              left: 156,
              top: 134,
              width: 360,
              height: 420,
              background: `linear-gradient(135deg, ${accent}, rgba(255,255,255,0.88))`,
            }}
          />
          <MotionPanel
            index={1}
            style={{
              right: 146,
              bottom: 118,
              width: 430,
              height: 180,
              borderRadius: 999,
              background: `linear-gradient(90deg, ${accent}, #ffffff)`,
            }}
          />
        </>
      ) : (
        <>
          <MotionPanel
            index={0}
            style={{
              left: 70,
              top: 80,
              width: 430,
              height: 245,
              background: `linear-gradient(135deg, ${accent}, #20b7ff)`,
            }}
          />
          <MotionPanel
            index={1}
            style={{
              right: 88,
              top: 130,
              width: 350,
              height: 455,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04))',
            }}
          />
          <MotionPanel
            index={2}
            style={{
              left: 156,
              bottom: 102,
              width: 620,
              height: 170,
              background: `linear-gradient(90deg, rgba(255,255,255,0.12), ${accent}66)`,
            }}
          />
        </>
      )}

      <div
        style={{
          position: 'absolute',
          left: 94,
          top: 92,
          padding: '13px 18px',
          border: `1px solid ${accent}`,
          color: accent,
          fontSize: 22,
          fontWeight: 950,
        }}
      >
        SCENE {index + 1}
      </div>

      <div
        style={{
          position: 'absolute',
          left: 94,
          right: layout === 'center-cta' ? 94 : 520,
          top: layout === 'center-cta' ? 235 : 188,
          transform: `translateY(${y}px)`,
        }}
      >
        <div
          style={{
            fontSize: fitText(headline, layout === 'center-cta' ? 92 : 76),
            lineHeight: 1.02,
            fontWeight: 950,
            letterSpacing: 0,
            textAlign: layout === 'center-cta' ? 'center' : 'left',
          }}
        >
          {headline}
        </div>

        <div
          style={{
            marginTop: 30,
            fontSize: 34,
            lineHeight: 1.25,
            color: 'rgba(255,255,255,0.72)',
            fontWeight: 750,
            textAlign: layout === 'center-cta' ? 'center' : 'left',
          }}
        >
          {subline}
        </div>
      </div>
    </AbsoluteFill>
  );
};

export const HermesVideo = ({
  prompt = 'VPS Render',
  subtitle = 'Generated on VPS',
  badge = 'HERMES x REMOTION',
  background = '#101114',
  accent = '#d8f36a',
  scenes = [],
}) => {
  const frame = useCurrentFrame();
  const {fps, durationInFrames} = useVideoConfig();

  const activeScenes = Array.isArray(scenes) && scenes.length > 0
    ? scenes.slice(0, 8)
    : defaultScenes.map((scene, index) => ({
        ...scene,
        headline: index === 0 ? prompt : scene.headline,
        subline: index === 0 ? subtitle : scene.subline,
      }));

  const sceneLength = Math.max(1, Math.floor(durationInFrames / activeScenes.length));
  const progress = clamp((frame / Math.max(1, durationInFrames - 1)) * 100, 0, 100);

  return (
    <AbsoluteFill
      style={{
        background,
        color: 'white',
        fontFamily: 'Inter, Pretendard, Noto Sans CJK KR, Arial, sans-serif',
        overflow: 'hidden',
      }}
    >
      {activeScenes.map((scene, index) => (
        <Sequence
          key={`${index}-${scene.headline || scene.type || 'scene'}`}
          from={index * sceneLength}
          durationInFrames={index === activeScenes.length - 1 ? durationInFrames - index * sceneLength : sceneLength + Math.floor(0.25 * fps)}
          premountFor={fps}
        >
          <Scene
            scene={scene}
            index={index}
            accent={scene.accent || accent}
            background={scene.background || background}
          />
        </Sequence>
      ))}

      <div
        style={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          width: `${progress}%`,
          height: 12,
          background: accent,
        }}
      />

      <div
        style={{
          position: 'absolute',
          right: 42,
          bottom: 34,
          color: 'rgba(255,255,255,0.42)',
          fontSize: 22,
          fontWeight: 800,
        }}
      >
        {badge}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
